import { makeClinicSlug } from './shared';
import cloudflareLoader from './cloudflare';
import { sanitizeImageUrl } from './images';
import { AlgoliaClinicV2, License, SpecialityChannel } from '@/types/algolia-types';

const GMAPS_QUERY_URL = 'https://www.google.com/maps/search/?api=1&query=';

export type MarkerLabelType = 'firstAvailability' | 'price';
export type MapMarkerItem = Pick<
  AlgoliaClinicV2,
  'objectID' | '_geoloc' | 'address' | 'name' | 'license' | 'logoUrl' | 'cap' | 'slug' | 'city' | 'ambulatoryId' | 'delayAvailability'
> & {
  specializations: Partial<Pick<AlgoliaClinicV2['specializations'], 'id' | 'specialityChannels' | 'label' | 'doctors'>>;
};

export const getMapLink = (address: string = '') => {
  const addressEncoded = encodeURIComponent(address);
  return `${GMAPS_QUERY_URL}${addressEncoded}`;
};

export const makeMarkersList = (items: MapMarkerItem[], markerLabelType: MarkerLabelType = 'price') => {
  const markers = [];

  for (const item of items) {
    if (
      !item?._geoloc?.lat ||
      !item?._geoloc?.lng ||
      !item?.name ||
      !item?.license ||
      item.specializations?.specialityChannels?.includes(SpecialityChannel.Online)
    ) {
      continue;
    }

    const doctorList = [...(item.specializations?.doctors ?? [])];
    const sortedDoctors = doctorList.sort((a, b) => (a.nextAvailability ?? Infinity) - (b.nextAvailability ?? Infinity));
    const selectedDoctor = sortedDoctors[0];

    const markerItem = {
      id: item.objectID,
      lat: item._geoloc.lat,
      lng: item._geoloc.lng,
      label: item?.name,
      price: selectedDoctor?.minPrice ?? selectedDoctor?.price,
      nextAvailability: selectedDoctor?.nextAvailability,
      serviceLabel: item.specializations?.label,
      markerLabelType,
      slug: makeClinicSlug(item, item?.specializations?.label ?? ''),
      isLicensed: item.license !== License.None,
      logo: item.logoUrl ? sanitizeImageUrl(cloudflareLoader({ src: item.logoUrl, width: 100 })) : undefined,
      address: `${item.address}, ${item.cap} ${item.city}`,
    };
    if (item.license !== License.None) markers.push(markerItem);
    else markers.unshift(markerItem);
  }

  return markers;
};

export const generateMapUuid = (items: { ambulatoryId: string }[]) => {
  const ids = [];
  for (const item of items) {
    if (!item?.ambulatoryId) continue;
    ids.push(item?.ambulatoryId);
  }
  return Buffer.from(ids.join(''), 'utf-8').toString('base64');
};
